// Hook personalizado para manejar la lógica del componente ReceiveContainer
// Custom hook for handling ReceiveContainer component logic
import { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import type { OrderResponse } from '../adapters';

export const useReceiveLogic = (_returnedId: number, onShowLocatePage?: () => void) => {
  const { t } = useTranslation('receiveModule');
  const { toast } = useToast();

  // Estados principales
  const [selectedOrder, setSelectedOrder] = useState<OrderResponse | null>(null);
  const [processedOrders, setProcessedOrders] = useState<OrderResponse[]>([]);
  const [processedProductsQuantities, setProcessedProductsQuantities] = useState<Record<string, number>>({});
  const [processedProductsTotals, setProcessedProductsTotals] = useState<Record<string, number>>({});
  const [scannedQuantities, setScannedQuantities] = useState<Record<string, number>>({});
  const [flashingProducts, setFlashingProducts] = useState<Set<string>>(new Set());
  const [ordersSortBy, setOrdersSortBy] = useState<string>('name_az');
  const [productsSortBy, setProductsSortBy] = useState<string>('name_az');
  const [selectedBatchIds, setSelectedBatchIds] = useState<Record<string, number | null>>({});
  const confirmModalRef = useRef<any>(null);

  // Funciones de ordenamiento
  const sortOrders = useCallback((orders: OrderResponse[], sortBy: string) => {
    return [...orders].sort((a, b) => {
      switch (sortBy) {
        case 'more_qty': {
          const aCount = a.lines.reduce((acc, line: any) => acc + (line.pack?.lines.length || 1), 0);
          const bCount = b.lines.reduce((acc, line: any) => acc + (line.pack?.lines.length || 1), 0);
          return bCount - aCount;
        }
        case 'less_qty': {
          const aCountLess = a.lines.reduce((acc, line: any) => acc + (line.pack?.lines.length || 1), 0);
          const bCountLess = b.lines.reduce((acc, line: any) => acc + (line.pack?.lines.length || 1), 0);
          return aCountLess - bCountLess;
        }
        case 'name_az':
          return a.ref.localeCompare(b.ref);
        case 'name_za':
          return b.ref.localeCompare(a.ref);
        default:
          return 0;
      }
    });
  }, []);

  const sortProducts = useCallback(
    (products: any[], sortBy: string) => {
      return [...products].sort((a, b) => {
        switch (sortBy) {
          case 'more_qty': {
            const aQty =
              (a as any).processedQuantity !== undefined
                ? (a as any).processedQuantity
                : processedProductsQuantities[a.code] || 0;
            const bQty =
              (b as any).processedQuantity !== undefined
                ? (b as any).processedQuantity
                : processedProductsQuantities[b.code] || 0;
            return bQty - aQty;
          }
          case 'less_qty': {
            const aQtyLess =
              (a as any).processedQuantity !== undefined
                ? (a as any).processedQuantity
                : processedProductsQuantities[a.code] || 0;
            const bQtyLess =
              (b as any).processedQuantity !== undefined
                ? (b as any).processedQuantity
                : processedProductsQuantities[b.code] || 0;
            return aQtyLess - bQtyLess;
          }
          case 'name_az':
            return a.name.localeCompare(b.name);
          case 'name_za':
            return b.name.localeCompare(a.name);
          default:
            return 0;
        }
      });
    },
    [processedProductsQuantities],
  );

  // Productos aplanados del pedido seleccionado
  const flattenedProducts = useMemo(() => {
    if (!selectedOrder)
      return [] as Array<{
        id: string;
        name: string;
        code: string;
        image: string;
        qty: number;
      }>;
    const productMap = new Map<string, any>();

    (selectedOrder.lines || []).forEach((line: any, index: number) => {
      // Línea simple con producto
      if (line?.product) {
        const product = line.product;
        const key = product.code || `BP${String(product.id)}`;

        if (productMap.has(key)) {
          const existing = productMap.get(key);
          existing.qty += line.qty ?? 1;
        } else {
          productMap.set(key, {
            id: String(product.id ?? line.product_id ?? line.id ?? index),
            name: product.name || line.name || t('table.product'),
            code: product.code || `BP${String(line.id).padStart(6, '0')}`,
            image: product.image_hash || '/imagen/example.webp',
            qty: line.qty ?? 1,
          });
        }
        return;
      }

      // Línea pack: expandir sub-líneas
      if (line?.pack?.lines?.length) {
        line.pack.lines.forEach((pl: any, idx: number) => {
          const p = pl.product || {};
          const key = p.code || `BP${String(p.id)}`;

          if (productMap.has(key)) {
            const existing = productMap.get(key);
            existing.qty += pl.qty ?? 1;
          } else {
            productMap.set(key, {
              id: String(p.id ?? pl.product_id ?? `${line.id}-${idx}`),
              name: p.name || line.name || t('table.product'),
              code: p.code || `BP${String(line.id).padStart(6, '0')}`,
              image: p.image_hash || '/imagen/example.webp',
              qty: pl.qty ?? 1,
            });
          }
        });
      }
    });

    return Array.from(productMap.values());
  }, [selectedOrder, t]);

  const productsCount = flattenedProducts.reduce((acc, it) => acc + (it.qty || 0), 0);

  // Productos procesados consolidados
  const processedProducts = useMemo(() => {
    const productMap = new Map<string, any>();

    processedOrders.forEach((order) => {
      (order.lines || []).forEach((line: any, index: number) => {
        // Línea simple con producto
        if (line?.product) {
          const product = line.product;
          const key = product.code || `BP${String(product.id)}`;

          if (productMap.has(key)) {
            const existing = productMap.get(key);
            existing.qty += line.qty ?? 1;
          } else {
            productMap.set(key, {
              id: String(product.id ?? line.product_id ?? line.id ?? index),
              name: product.name || line.name || t('table.product'),
              code: product.code || `BP${String(line.id).padStart(6, '0')}`,
              image: product.image_hash || '/imagen/example.webp',
              qty: line.qty ?? 1,
            });
          }
          return;
        }

        // Línea pack: expandir sub-líneas
        if (line?.pack?.lines?.length) {
          line.pack.lines.forEach((pl: any, idx: number) => {
            const p = pl.product || {};
            const key = p.code || `BP${String(p.id)}`;

            if (productMap.has(key)) {
              const existing = productMap.get(key);
              existing.qty += pl.qty ?? 1;
            } else {
              productMap.set(key, {
                id: String(p.id ?? pl.product_id ?? `${line.id}-${idx}`),
                name: p.name || line.name || t('table.product'),
                code: p.code || `BP${String(line.id).padStart(6, '0')}`,
                image: p.image_hash || '/imagen/example.webp',
                qty: pl.qty ?? 1,
              });
            }
          });
        }
      });
    });

    return Array.from(productMap.values());
  }, [processedOrders, t]);

  // Listas ordenadas
  const sortedProcessedOrders = useMemo(() => {
    return sortOrders(processedOrders, ordersSortBy);
  }, [processedOrders, ordersSortBy, sortOrders]);

  const sortedProcessedProducts = useMemo(() => {
    return sortProducts(processedProducts, productsSortBy);
  }, [processedProducts, productsSortBy, sortProducts]);

  // Handlers
  const handleOrderFound = useCallback((order: OrderResponse) => {
    setSelectedOrder(order);
    setScannedQuantities({});
    setSelectedBatchIds({});
  }, []);

  const handleBatchChange = useCallback((productId: string, batchId: number | null) => {
    setSelectedBatchIds((prev) => ({ ...prev, [productId]: batchId }));
  }, []);

  const handleOrdersSort = useCallback((sortBy: string) => {
    setOrdersSortBy(sortBy);
  }, []);

  const handleProductsSort = useCallback((sortBy: string) => {
    setProductsSortBy(sortBy);
  }, []);

  const handleFinishProcessed = useCallback(() => {
    console.log('Finalizando productos procesados...');
  }, []);

  const handleLocateProcessed = useCallback(() => {
    onShowLocatePage?.();
  }, [onShowLocatePage]);

  const hasScannedProducts = useCallback(() => {
    return Object.values(scannedQuantities).some((qty) => qty > 0);
  }, [scannedQuantities]);

  // 🎯 Validación de cantidades antes de procesar
  const validateQuantities = useCallback(() => {
    if (!selectedOrder) return { type: 'none', message: '', title: '' };

    let hasExcess = false;
    let hasMissing = false;
    let allComplete = true;

    flattenedProducts.forEach((product) => {
      const scannedQty = scannedQuantities[product.code] || 0;
      const expectedQty = product.qty;

      if (scannedQty > expectedQty) {
        hasExcess = true;
        allComplete = false;
      } else if (scannedQty < expectedQty) {
        hasMissing = true;
        allComplete = false;
      }
    });

    if (hasExcess) {
      return {
        type: 'excess',
        title: t('validation.excess_units.title'),
        message: t('validation.excess_units.message'),
      };
    } else if (hasMissing) {
      return {
        type: 'missing',
        title: t('validation.missing_units.title'),
        message: t('validation.missing_units.message'),
      };
    } else if (allComplete) {
      return {
        type: 'complete',
        title: t('validation.close_returned.title'),
        message: t('validation.close_returned.message'),
      };
    }

    return { type: 'none', message: '', title: '' };
  }, [selectedOrder, scannedQuantities, flattenedProducts, t]);

  const processOrder = useCallback(() => {
    if (selectedOrder) {
      setProcessedOrders((prev) => [...prev, selectedOrder]);
      setProcessedProductsQuantities((prev) => {
        const newQuantities = { ...prev };
        Object.entries(scannedQuantities).forEach(([code, qty]) => {
          newQuantities[code] = (newQuantities[code] || 0) + qty;
        });
        return newQuantities;
      });

      setProcessedProductsTotals((prev) => {
        const newTotals = { ...prev };
        flattenedProducts.forEach((product) => {
          newTotals[product.code] = (newTotals[product.code] || 0) + product.qty;
        });
        return newTotals;
      });
      setSelectedOrder(null);
      setScannedQuantities({});
    }
  }, [selectedOrder, scannedQuantities, flattenedProducts]);

  const handleProcessOrder = useCallback(() => {
    if (!hasScannedProducts()) {
      // Sin productos escaneados
      confirmModalRef.current?.open(
        () => {
          processOrder();
        },
        undefined,
        '¿Pedido sin productos?',
        'El pedido ha llegado al almacén sin productos o con una cantidad incorrecta a la indicada.',
      );
      return;
    }

    // Validar cantidades
    const validation = validateQuantities();

    if (validation.type === 'none') {
      // Sin problemas, procesar directamente
      processOrder();
    } else {
      // Mostrar modal de confirmación según el tipo de validación
      confirmModalRef.current?.open(
        () => {
          processOrder();
        },
        undefined,
        validation.title,
        validation.message,
      );
    }
  }, [hasScannedProducts, processOrder, validateQuantities]);

  const handleProductScan = useCallback(
    (code: string) => {
      const normalized = code.trim();
      if (!normalized) return;

      const match = flattenedProducts.find((p) => p.code === normalized);
      if (!match) {
        toast({
          title: t('toasts.product_not_found.title'),
          description: t('toasts.product_not_found.description', {
            code: normalized,
          }),
          variant: 'destructive',
        });
        return;
      }

      setFlashingProducts((prev) => new Set(prev).add(match.code));

      setTimeout(() => {
        setFlashingProducts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(match.code);
          return newSet;
        });
      }, 300);

      setScannedQuantities((prev) => {
        const current = prev[match.code] || 0;
        const next = current + 1; // Permitir cantidades excedentes
        return { ...prev, [match.code]: next };
      });

      // Mostrar toast de unidad agregada
      toast({
        title: t('toasts.unit_added.title'),
        description: t('toasts.unit_added.description'),
        variant: 'default',
      });
    },
    [flattenedProducts, t, toast],
  );

  return {
    // Estados
    selectedOrder,
    processedOrders,
    processedProductsQuantities,
    processedProductsTotals,
    scannedQuantities,
    flashingProducts,
    ordersSortBy,
    productsSortBy,
    confirmModalRef,

    // Datos procesados
    flattenedProducts,
    productsCount,
    processedProducts,
    sortedProcessedOrders,
    sortedProcessedProducts,

    // Handlers
    handleOrderFound,
    handleOrdersSort,
    handleProductsSort,
    handleFinishProcessed,
    handleLocateProcessed,
    handleProcessOrder,
    handleProductScan,
    handleBatchChange,
    validateQuantities,

    // Batch state
    selectedBatchIds,
  };
};
